import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface ChartData {
  title?: string;
  labels?: string[];
  datasets?: Array<{ label?: string; values?: number[] }>;
  currency?: string;
  chart_type?: string;
}

interface GroupedBarChartProps {
  chart: ChartData;
  currency?: string;
}

const PIE_COLORS = ["#4AC08C", "#3AA87A", "#2E8E68", "#247457", "#7FD3AE", "#C9EFE0", "#6BBF8A", "#5AAF7C"];

const formatAxisValue = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const absNum = Math.abs(num);
  if (absNum >= 1_000_000) return `${(absNum / 1_000_000).toFixed(1)}M`;
  if (absNum >= 1_000) return `${(absNum / 1_000).toFixed(1)}K`;
  return String(absNum);
};

const formatBarLabel = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  return num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
};

const formatHover = (num: number, currency?: string) => {
  if (!currency) return String(num);
  const suffix = Math.abs(num) >= 1_000_000 ? "M" : Math.abs(num) >= 1_000 ? "K" : "";
  const absNum = Math.abs(num);
  const shortNum = absNum >= 1_000_000 ? (absNum / 1_000_000).toFixed(1) : absNum >= 1_000 ? (absNum / 1_000).toFixed(1) : String(absNum);
  const numStr = num < 0 ? `-${shortNum}${suffix}` : `${shortNum}${suffix}`;
  return `${numStr} ${currency}`;
};

const truncateLabel = (str: string, maxLen: number) => {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 2) + "…";
};

export function GroupedBarChart({ chart, currency }: GroupedBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const labels = chart?.labels || [];
  const datasets = chart?.datasets || [];
  const chartCurrency = currency || chart?.currency || "AED";

  const data = useMemo(() => {
    return labels.map((label: string, i: number) => {
      const obj: Record<string, number | string> = { name: label };
      datasets.forEach((ds, idx) => { obj[ds.label || `dataset_${idx}`] = ds.values?.[i] ?? 0; });
      return obj;
    });
  }, [labels, datasets]);

  const dataKeys = useMemo(() => datasets.map((ds) => ds.label || `dataset_${datasets.indexOf(ds)}`), [datasets]);
  const showValueLabels = useMemo(() => data.length * datasets.length <= 12, [data, datasets]);
  const needRotate = useMemo(() => labels.some((l) => l.length > 6), [labels]);
  
  const chartHeight = Math.max(data.length * 48 + datasets.length * 35 + 150, 300);
  const margin = { top: 25, right: 15, bottom: needRotate ? 100 : 80, left: 60 };

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const containerWidth = svgRef.current.parentElement?.clientWidth || 300;
    const width = Math.max(containerWidth - 20, 200);
    const height = chartHeight;
    const barWidth = width - margin.left - margin.right;
    const barHeight = height - margin.top - margin.bottom;

    const x0 = d3.scaleBand().domain(data.map((d) => d.name as string)).range([0, barWidth]).padding(0.2);
    const x1 = d3.scaleBand().domain(dataKeys).range([0, x0.bandwidth()]).padding(0.1);

    const maxValue = d3.max(data, (d) => Math.max(...dataKeys.map((key) => Math.abs(Number(d[key])) || 0)) || 0);
    const y = d3.scaleLinear().domain([0, maxValue * 1.25]).range([barHeight, 0]);

    const chartGroup = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const yTicks = y.ticks(4).filter(t => t > 0);
    chartGroup.selectAll(".grid-line").data(yTicks).enter()
      .append("line").attr("x1", 0).attr("x2", barWidth).attr("y1", d => y(d)).attr("y2", d => y(d))
      .attr("stroke", "#E5E7EB").attr("stroke-dasharray", "3,3");

    chartGroup.selectAll(".y-tick").data(yTicks).enter()
      .append("text").attr("x", -6).attr("y", d => y(d)).attr("dy", "0.35em").attr("text-anchor", "end")
      .attr("font-size", "10px").attr("fill", "#6B7280").text(d => formatAxisValue(d, chartCurrency));

    data.forEach((d) => {
      const g = chartGroup.append("g").attr("transform", `translate(${x0(d.name as string)},0)`);
      dataKeys.forEach((key, i) => {
        const value = Number(d[key]) || 0;
        const absValue = Math.abs(value);
        const barY = y(absValue);
        g.append("rect").attr("x", x1(key) || 0).attr("y", barY).attr("width", x1.bandwidth())
          .attr("height", barHeight - barY).attr("fill", PIE_COLORS[i % PIE_COLORS.length]).attr("rx", 3).style("cursor", "pointer")
          .on("mouseenter", function (event) {
            d3.select(this).attr("opacity", "0.85");
            if (tooltipRef.current) {
              tooltipRef.current.style.opacity = "1";
              tooltipRef.current.innerHTML = `<strong>${key}</strong> - ${d.name}<br/>${formatHover(value, chartCurrency)}`;
              tooltipRef.current.style.left = `${event.offsetX + 15}px`;
              tooltipRef.current.style.top = `${event.offsetY - 10}px`;
            }
          })
          .on("mouseleave", function () {
            d3.select(this).attr("opacity", "1");
            if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
          });

        if (showValueLabels && x1.bandwidth() > 28 && absValue > 0) {
          g.append("text").attr("x", (x1(key) || 0) + x1.bandwidth() / 2).attr("y", barY - 5)
            .attr("text-anchor", "middle").attr("font-size", "8px").attr("font-weight", "600").attr("fill", "#374151")
            .text(formatBarLabel(value, chartCurrency));
        }
      });
    });

    const xAxis = chartGroup.append("g").attr("transform", `translate(0,${barHeight})`)
      .call(d3.axisBottom(x0).tickSize(0).tickFormat((d) => needRotate ? truncateLabel(String(d), 12) : truncateLabel(String(d), 14)));
    xAxis.selectAll("text").attr("font-size", "10px").attr("fill", "#6B7280");
    if (needRotate) xAxis.selectAll("text").attr("transform", "rotate(-30)").attr("text-anchor", "end");
    xAxis.selectAll(".domain").remove();
  }, [data, dataKeys, chartCurrency, showValueLabels, needRotate, chartHeight, margin]);

  return (
    <div className="ai-summary-panel__card" ref={containerRef}>
      <p className="ai-summary-panel__card-title">{chart?.title || ""}</p>
      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "10px" }}>
        {dataKeys.map((key, i) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span style={{ fontSize: "10px", color: "#6B7280" }}>{key}</span>
          </div>
        ))}
      </div>
      <div style={{ position: "relative", overflow: "visible", height: chartHeight, width: "100%" }}>
        <svg ref={svgRef} width="100%" height={chartHeight} style={{ display: "block" }}></svg>
        <div ref={tooltipRef} style={{
          position: "absolute", backgroundColor: "white", padding: "8px 12px", borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)", fontSize: "11px", color: "#374151", opacity: 0,
          transition: "opacity 0.15s", pointerEvents: "none", zIndex: 10, border: "1px solid #E5E7EB", lineHeight: 1.4,
        }} />
      </div>
    </div>
  );
}

export default GroupedBarChart;